import { createSlice } from '@reduxjs/toolkit';
import { authApi } from '../auth/authApi'
import { IChatboxResponse, IChatResponse } from '../../interfaces/chats/IChatResponse';
import { chatboxApi } from '../chatbox/chatboxApi';
import { userAPI } from '../user/userApi';
import Cookies from 'js-cookie';
import { toNaiveISO } from '../../utils/date';


const initialState: {
	topicId: string
	chatTitle: string,
	messages: IChatboxResponse[],
	authMessages: IChatResponse[],
	isSending: boolean,
	error?: string | null,
	finishedFeedback: boolean;
} = {
	topicId: '',
	chatTitle: '',
	messages: [],
	authMessages: [],
	isSending: false,
	error: null,
	finishedFeedback: false
};

const chatSlice = createSlice({
	name: 'chat',
	initialState,
	reducers: {
		setChatId: (state, action) => {
			state.topicId = action.payload;
		},
		setAuthChats: (state, action) => {
			state.authMessages = action.payload;
		},
		setChatMessages: (state, action) => {
			state.messages = action.payload
		},
		removeChatMessage: (state, action) => {
			state.messages = state.messages.filter(message => message.requestId !== action.payload) 
		},
		resetFinishedFeedback: state => {
			state.finishedFeedback = false;
		},
	},
	extraReducers: (builder) => {
		builder
			.addMatcher(
				authApi.endpoints.authWithAgent.matchPending,
				(state, action) => {
					state.authMessages = action.meta.arg.originalArgs.chatData.map(
						(arg: IChatResponse, index: number) =>
							index === action.meta.arg.originalArgs.chatData.length - 1 ?
								{
									user: {
										...arg.user,
										requestId: action.meta.requestId,
										type: 'pending'
									}
								}
								: arg);
					state.isSending = true;
				}
			)
			.addMatcher(
				authApi.endpoints.authWithAgent.matchRejected,
				(state, action) => {
					state.isSending = false;
					state.authMessages = state.authMessages.map(
						(arg: IChatResponse) =>
							arg.user?.requestId === action.meta.requestId ?
								{
									user: {
										...arg.user,
										type: 'error'
									}
								}
								: arg);
					const errorObj = action as { payload: { data: { message: string } } };
					state.error = errorObj.payload.data.message || action.error.message;
				}
			)
			.addMatcher(
				authApi.endpoints.authWithAgent.matchFulfilled,
				(state, action) => {
					state.isSending = false;
					if (action.payload.data.metadata) {
						const { accessToken, refreshToken, user } = action.payload.data.metadata;
						if (accessToken) {
							Cookies.set('accessToken', accessToken, { 
								path: '/',
								secure: true,
								sameSite: 'None',
							});
						}
						if (refreshToken) {
							Cookies.set('refreshToken', refreshToken, { 
								path: '/',
								secure: true,
								sameSite: 'None',
							});
						}
						if (user?.type) {
							Cookies.set('roles', user.type, { 
								path: '/',
								secure: true,
								sameSite: 'None',
							});
						}
					}
					state.authMessages = [...action.payload.data.chatData]
				}
			)

			.addMatcher(
				userAPI.endpoints.signUpWithAgent.matchPending,
				(state, action) => {
					state.authMessages = action.meta.arg.originalArgs.chatData.map(
						(arg: IChatResponse, index: number) =>
							index === action.meta.arg.originalArgs.chatData.length - 1 ?
								{
									user: {
										...arg.user,
										requestId: action.meta.requestId,
										type: 'pending'
									}
								}
								: arg);
					state.isSending = true;
				}
			)
			.addMatcher(
				userAPI.endpoints.signUpWithAgent.matchRejected,
				(state, action) => {
					state.isSending = false;
					state.authMessages = state.authMessages.map(
						(arg: IChatResponse) =>
							arg.user?.requestId === action.meta.requestId ?
								{
									user: {
										...arg.user,
										type: 'error'
									}
								}
								: arg);
					const errorObj = action as { payload: { data: { message: string } } };
					state.error = errorObj.payload.data.message || action.error.message;
				}
			)
			.addMatcher(
				userAPI.endpoints.signUpWithAgent.matchFulfilled,
				(state, action) => {
					state.isSending = false;
					state.authMessages = [...action.payload.data.chatData]
				}
			)

			.addMatcher(
				authApi.endpoints.signInWithAgent.matchPending,
				(state, action) => {
					state.authMessages = action.meta.arg.originalArgs.chatData.map(
						(arg: IChatResponse, index: number) =>
							index === action.meta.arg.originalArgs.chatData.length - 1 ?
								{
									user: {
										...arg.user,
										requestId: action.meta.requestId,
										type: 'pending'
									}
								}
								: arg);
					state.isSending = true;
				}
			)
			.addMatcher(
				authApi.endpoints.signInWithAgent.matchRejected,
				(state, action) => {
					state.isSending = false;
					state.authMessages = state.authMessages.map(
						(arg: IChatResponse) =>
							arg.user?.requestId === action.meta.requestId ?
								{
									user: {
										...arg.user,
										type: 'error'
									}
								}
								: arg);
					const errorObj = action as { payload: { data: { message: string } } };
					state.error = errorObj.payload.data.message || action.error.message;
				}
			)
			.addMatcher(
				authApi.endpoints.signInWithAgent.matchFulfilled,
				(state, action) => {
					state.isSending = false;
					if (action.payload.data.data.metadata) {
						const { accessToken, refreshToken, user } = action.payload.data.data.metadata;
						if (accessToken) {
							Cookies.set('accessToken', accessToken, { 
								path: '/',
								secure: true,
								sameSite: 'None',
							});
						}
						if (refreshToken) {
							Cookies.set('refreshToken', refreshToken, { 
								path: '/',
								secure: true,
								sameSite: 'None',
							});
						}
						if (user?.type) {
							Cookies.set('roles', user?.type, { 
								path: '/',
								secure: true,
								sameSite: 'None',
							});
						}
					}
					state.authMessages = [...action.payload.data.data.chatData]
				}
			)

			.addMatcher(
				chatboxApi.endpoints.chatbox.matchPending,
				(state, action) => {
					state.messages.filter(message => message._id !== 'typing');
						state.messages.push({
						_id: action.meta.requestId,
						message: action.meta.arg.originalArgs.message || '',
						files: action.meta.arg.originalArgs.files,
						requestId: action.meta.requestId,
						type: 'pending',
						msgType: action.meta.arg.originalArgs.files?.length ? 'file' : 'message',
						updatedAt: toNaiveISO(),
						createdAt: toNaiveISO(),
						userId: '',
						topic: '',
						user: {
							name: 'my name'
						}
					},
						{
							_id: action.meta.requestId,
							message: '',
							requestId: action.meta.requestId,
							type: 'pending',
							msgType: 'message',
							updatedAt: toNaiveISO(),
							createdAt: toNaiveISO(),
							userId: '',
							topic: '',
							agent: {
								name: 'my name'
							}
						});
					state.isSending = true;
				}
			)
			.addMatcher(
				chatboxApi.endpoints.chatbox.matchRejected,
				(state, action) => {
					state.isSending = false;
					state.messages = state.messages.filter(message => !(message.requestId === action.meta.requestId && message.agent)).map(
						(arg: IChatboxResponse) =>
							arg.requestId === action.meta.requestId ?
								({
									...arg,
									type: 'error'
								}
								)
								: arg);
					state.error = action.error.message;
				}
			)
			.addMatcher(
				chatboxApi.endpoints.chatbox.matchFulfilled,
				(state, action) => {
					state.isSending = false;
					const updated = state.messages = state.messages.map(
						(arg: IChatboxResponse) =>
							arg.requestId === action.meta.requestId ?
								arg.user ? {
									...arg,
									type: 'success'
								} : { ...arg, ...action.payload.data, type: 'success', instantReply: true, _id: action.payload.data.agentMessageId || '' }
								: arg);
					state.topicId = action.payload.data.topic;
					if (action.payload.data.stage === 'wrap_up') {
						state.finishedFeedback = true;
					}
					state.messages = [...updated] // {...action.payload.data, instantReply: true, agent: {name: 'saga'}}]
				}
			)

	},
});

export const { setChatId, setAuthChats, setChatMessages, removeChatMessage, resetFinishedFeedback } = chatSlice.actions;
export default chatSlice.reducer;